import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Typography,
  Paper,
  Toolbar,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import AddIcon from "@mui/icons-material/Add";
import FilterListIcon from "@mui/icons-material/FilterList";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CloseIcon from "@mui/icons-material/Close";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import axios from "axios";
import RowServiceTypeActionMenu from "./RowServiceTypeActionMenu";
import RowServiceChargeActionsMenu from "./RowServiceChargeActionsMenu";

export default function ServiceTypesManagement() {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);
  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
  const openActionsMenu = Boolean(actionsAnchorEl);

  // Base API URL from environment
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // Common UI state
  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  // Snackbar
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // ServiceType -> ServiceCharges modal state
  const [scDialogOpen, setSCDialogOpen] = useState(false);
  const [scViewOpen, setSCViewOpen] = useState(false);
  const [scRows, setSCRows] = useState([]);
  const [scColumns, setSCColumns] = useState([]);
  const [scEditing, setSCEditing] = useState(null);
  const [scServiceTypeName, setSCServiceTypeName] = useState("");
  const [serviceChargeFormData, setServiceChargeFormData] = useState({
    academic_year: "",
    service_description: "",
    service_charge: "",
    service_type_id: "",
  });

  const user = useMemo(() => {
    const u = sessionStorage.getItem("user");
    return u ? JSON.parse(u) : null;
  }, []);

  const getAuthHeaders = () => {
    const token = sessionStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const showSnackbar = (msg, sev = "success") => {
    setSnackbarMessage(msg);
    setSnackbarSeverity(sev);
    setSnackbarOpen(true);
  };

  const fetchData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const url = `${API_BASE_URL}/api/service-types/${user.client_id}/${user.college_id}`;
      const res = await axios.get(url, { headers: getAuthHeaders() });
      let data = [];
      if (Array.isArray(res.data)) data = res.data;
      else if (res.data && Array.isArray(res.data.data)) data = res.data.data;
      setAllRows(data || []);
      setRows(data || []);
    } catch (err) {
      console.error("Error fetching service types", err);
      showSnackbar("Failed to load service types", "error");
      setAllRows([]);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [user, API_BASE_URL]);

  useEffect(() => {
    if (user) fetchData();
  }, [user, fetchData]);

  useEffect(() => {
    let filtered = [...allRows];

    if (search) {
      const s = search.toLowerCase();
      filtered = filtered.filter((r) => (r.service_type_name || "").toLowerCase().includes(s));
    }

    setRows(filtered);
  }, [search, allRows]);

  // --- ServiceType -> ServiceCharges helpers ---
  const openServiceChargeDialog = (service_type_id, service_type_name) => {
    setServiceChargeFormData({
      academic_year: "",
      service_description: "",
      service_charge: "",
      service_type_id: service_type_id || "",
    });
    setSCEditing(null);
    setSCServiceTypeName(service_type_name || "");
    setSCDialogOpen(true);
  };

  const openServiceChargesView = async (service_type_id, service_type_name) => {
    if (!user) return;
    try {
      // fetch all charges for this college then filter by service_type_id
      const url = `${API_BASE_URL}/api/service-charges/${user.client_id}/${user.college_id}`;
      const res = await axios.get(url, { headers: getAuthHeaders() });
      const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
      const filtered = data.filter((c) => String(c.service_type_id) === String(service_type_id));

      // prepare simple columns for modal table
      setSCColumns([
        { field: "service_charge_id", headerName: "ID", flex: 0.3, minWidth: 60 },
        { field: "academic_year", headerName: "Academic Year", flex: 0.6, minWidth: 120 },
        { field: "service_description", headerName: "Description", flex: 1, minWidth: 180 },
        { field: "service_charge", headerName: "Charge", flex: 0.5, minWidth: 100 },
        {
          field: "actions",
          headerName: "Actions",
          sortable: false,
          flex: 0.6,
          minWidth: 140,
          renderCell: (params) => (
            <RowServiceChargeActionsMenu
              row={params.row}
              onEdit={(r) => {
                // populate dialog with existing charge data
                setSCEditing(r);
                setServiceChargeFormData({
                  academic_year: r.academic_year || "",
                  service_description: r.service_description || "",
                  service_charge: r.service_charge || "",
                  service_type_id: r.service_type_id || service_type_id,
                });
                setSCDialogOpen(true);
              }}
              onDelete={async (id) => {
                if (!window.confirm("Are you sure you want to delete this service charge?")) return;
                try {
                  const deleteUrl = `${API_BASE_URL}/api/service-charges/${user.client_id}/${user.college_id}/${id}`;

                  await axios.delete(deleteUrl, {
                    headers: getAuthHeaders(),
                  });
                  showSnackbar("Service charge deleted", "success");
                  // refresh lists
                  fetchData();
                  openServiceChargesView(service_type_id, service_type_name);
                } catch (err) {
                  console.error("Error deleting service charge:", err);
                  showSnackbar(
                    err?.response?.data?.message || "Failed to delete service charge",
                    "error"
                  );
                }
              }}
              hasPermissionDirect={() => true}
            />
          ),
        },
      ]);
      setSCRows(filtered || []);
      setSCServiceTypeName(service_type_name || "");
      setSCViewOpen(true);
    } catch (err) {
      console.error("Error fetching service charges", err);
      showSnackbar("Failed to load service charges", "error");
    }
  };

  const handleServiceChargeSave = async () => {
    if (!serviceChargeFormData.service_description?.trim()) {
      showSnackbar("Service description is required", "error");
      return;
    }
    try {
      const serviceCharge = serviceChargeFormData.service_charge
        ? Number(serviceChargeFormData.service_charge)
        : null;
      const serviceTypeId = serviceChargeFormData.service_type_id
        ? Number(serviceChargeFormData.service_type_id)
        : null;

      const payload = {
        academic_year: serviceChargeFormData.academic_year,
        service_description: serviceChargeFormData.service_description,
        service_charge: serviceCharge,
        service_type_id: serviceTypeId,
        college_id: user.college_id,
        created_by: user.user_email || "system",
      };

      let res;
      if (scEditing && (scEditing.service_charge_id || scEditing.id)) {
        // Update existing charge
        payload.modified_by = user.user_email || "system";
        const cid = scEditing.service_charge_id || scEditing.id;
        res = await axios.put(
          `${API_BASE_URL}/api/service-charges/${user.client_id}/${user.college_id}/${cid}`,
          payload,
          { headers: getAuthHeaders() }
        );
      } else {
        // Create new charge
        res = await axios.post(
          `${API_BASE_URL}/api/service-charges/${user.client_id}/${user.college_id}`,
          payload,
          { headers: getAuthHeaders() }
        );
      }

      showSnackbar(
        res.data.message || (scEditing ? "Service charge updated" : "Service charge created"),
        "success"
      );
      setSCDialogOpen(false);
      setSCEditing(null);
      // refresh service type list and modal list
      fetchData();
      if (serviceChargeFormData.service_type_id) {
        openServiceChargesView(serviceChargeFormData.service_type_id, scServiceTypeName);
      }
    } catch (err) {
      console.error("Error saving service charge:", err);
      showSnackbar(err?.response?.data?.message || "Error saving service charge", "error");
    }
  };

  const handleEdit = (row) => {
    setEditing(row);
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this service type?")) return;

    try {
      // Check if service charges exist for this service type before deleting
      const chargesUrl = `${API_BASE_URL}/api/service-charges/${user.client_id}/${user.college_id}`;
      const chargesRes = await axios.get(chargesUrl, { headers: getAuthHeaders() });
      const chargesData = Array.isArray(chargesRes.data)
        ? chargesRes.data
        : chargesRes.data?.data || [];

      const hasCharges = chargesData.some((c) => String(c.service_type_id) === String(id));
      if (hasCharges) {
        showSnackbar(
          "Cannot delete service type: there are service charges associated with this type. Delete those charges first.",
          "error"
        );
        return;
      }

      const res = await axios.delete(
        `${API_BASE_URL}/api/service-types/${user.client_id}/${user.college_id}/${id}`,
        { headers: getAuthHeaders() }
      );
      showSnackbar(res.data.message || "Service type deleted", "success");
      fetchData();
    } catch (err) {
      console.error("Error deleting service type", err);
      showSnackbar(err?.response?.data?.message || "Error deleting service type", "error");
    }
  };

  // Actions menu handlers
  const handleActionsClick = (e) => setActionsAnchorEl(e.currentTarget);
  const handleActionsClose = () => setActionsAnchorEl(null);

  // CSV Download function
  const handleDownloadCsv = async () => {
    try {
      const headers = ["service_type_id", "service_type_name", "service_type_desc"];
      const csvRows = [headers.join(",")];
      rows.forEach((r) => {
        const row = headers
          .map((h) => `"${(r[h] ?? "").toString().replace(/"/g, '""')}"`)
          .join(",");
        csvRows.push(row);
      });
      const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", "service_types.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      showSnackbar("CSV downloaded successfully", "success");
    } catch (err) {
      console.warn("CSV download failed", err);
      showSnackbar("Error downloading CSV", "error");
    }
  };

  const handleDownloadPdf = async () => {
    try {
      const { jsPDF } = await import("jspdf");
      await import("jspdf-autotable");

      const doc = new jsPDF();
      const headers = ["ID", "Service Type Name", "Description"];
      const dataRows = rows.map((r) => [
        r.service_type_id || "",
        r.service_type_name || "",
        r.service_type_desc || "",
      ]);

      doc.autoTable({ head: [headers], body: dataRows, startY: 10 });
      doc.save("service_types.pdf");
      showSnackbar("PDF downloaded successfully", "success");
    } catch (err) {
      console.error("PDF download failed:", err);
      showSnackbar("Error downloading PDF", "error");
    }
  };

  const handleUploadCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/api/service-types/${user.client_id}/${user.college_id}/upload/csv`,
        formData,
        { headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" } }
      );
      showSnackbar(data.message || "CSV uploaded successfully", "success");
      fetchData();
    } catch (err) {
      console.error(err);
      showSnackbar(err?.response?.data?.message || "Upload failed", "error");
    } finally {
      e.target.value = "";
    }
  };

  const [serviceTypeFormData, setServiceTypeFormData] = useState({
    service_type_name: "",
    service_type_desc: "",
  });

  useEffect(() => {
    if (editing) {
      setServiceTypeFormData({
        service_type_name: editing.service_type_name || "",
        service_type_desc: editing.service_type_desc || "",
      });
    } else {
      setServiceTypeFormData({ service_type_name: "", service_type_desc: "" });
    }
  }, [editing]);

  const handleSubmit = async () => {
    if (!serviceTypeFormData.service_type_name?.trim()) {
      showSnackbar("Service type name is required", "error");
      return;
    }
    try {
      let res;
      const payload = {
        ...serviceTypeFormData,
        college_id: user.college_id,
        created_by: user.user_email || "system",
      };

      if (editing) {
        res = await axios.put(
          `${API_BASE_URL}/api/service-types/${user.client_id}/${user.college_id}/${editing.service_type_id}`,
          payload,
          { headers: getAuthHeaders() }
        );
      } else {
        res = await axios.post(
          `${API_BASE_URL}/api/service-types/${user.client_id}/${user.college_id}`,
          payload,
          { headers: getAuthHeaders() }
        );
      }
      showSnackbar(res.data.message || (editing ? "Updated" : "Created"), "success");
      setOpen(false);
      setEditing(null);
      fetchData();
    } catch (err) {
      console.error("Error saving service type", err);
      showSnackbar(err?.response?.data?.message || "Error saving service type", "error");
    }
  };

  const columns = [
    { field: "service_type_id", headerName: "ID", flex: 0.4, minWidth: 60 },
    { field: "service_type_name", headerName: "Service Type Name", flex: 1.2, minWidth: 180 },
    { field: "service_type_desc", headerName: "Description", flex: 1.2, minWidth: 180 },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      flex: 0.8,
      minWidth: 140,
      renderCell: (params) => (
        <RowServiceTypeActionMenu
          row={params.row}
          onEdit={(r) => handleEdit(r)}
          onDelete={(id) => handleDelete(id)}
          onCreateServiceCharge={(service_type_id, service_type_name) =>
            openServiceChargeDialog(service_type_id, service_type_name)
          }
          onViewServiceCharges={(service_type_id, service_type_name) =>
            openServiceChargesView(service_type_id, service_type_name)
          }
          hasPermissionDirect={() => true}
        />
      ),
    },
  ];

  // Use React Router navigation
  const navigate = useNavigate();
  const handleGoToManageServiceTypes = () => {
    navigate("/client/lookup/manage-service-types");
  };

  return (
    <Box p={2}>
      <Paper elevation={1}>
        <Toolbar sx={{ gap: 1 }}>
          <Typography variant="h5" sx={{ flexGrow: 1 }}>
            SERVICE TYPES MANAGEMENT
          </Typography>

          <Button
            variant="contained"
            startIcon={<MoreVertIcon />}
            sx={{
              backgroundColor: "#1976d2",
              color: "#fff",
              "&:hover": { backgroundColor: "#115293" },
            }}
            onClick={handleActionsClick}
          >
            ACTIONS
          </Button>
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={(e) => setAnchorEl(e.currentTarget)}
          >
            Filters
          </Button>
          <Button
            variant="contained"
            color="primary"
            sx={{ ml: 2 }}
            onClick={handleGoToManageServiceTypes}
          >
            Manage Service Types
          </Button>
          <Menu anchorEl={actionsAnchorEl} open={openActionsMenu} onClose={handleActionsClose}>
            <MenuItem disableRipple>
              <Button
                fullWidth
                variant="contained"
                startIcon={<AddIcon />}
                sx={{
                  backgroundColor: "#1976d2",
                  color: "#fff",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#115293" },
                }}
                onClick={() => {
                  handleActionsClose();
                  setOpen(true);
                }}
              >
                ADD SERVICE TYPE
              </Button>
            </MenuItem>
            <MenuItem disableRipple>
              <Button
                fullWidth
                variant="contained"
                startIcon={<DownloadIcon />}
                sx={{
                  backgroundColor: "#4caf50",
                  color: "#fff",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#357a38" },
                }}
                onClick={() => {
                  handleActionsClose();
                  handleDownloadCsv();
                }}
              >
                DOWNLOAD CSV
              </Button>
            </MenuItem>
            <MenuItem disableRipple>
              <Button
                fullWidth
                variant="contained"
                startIcon={<PictureAsPdfIcon />}
                sx={{
                  backgroundColor: "#e53935",
                  color: "#fff",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#c62828" },
                }}
                onClick={() => {
                  handleActionsClose();
                  handleDownloadPdf();
                }}
              >
                DOWNLOAD PDF
              </Button>
            </MenuItem>
            <MenuItem disableRipple>
              <Button
                fullWidth
                variant="contained"
                component="label"
                startIcon={<UploadFileIcon />}
                sx={{
                  backgroundColor: "#ff9800",
                  color: "#fff",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#c77700" },
                }}
              >
                UPLOAD CSV
                <input
                  hidden
                  type="file"
                  accept=".csv"
                  onChange={(e) => {
                    handleActionsClose();
                    handleUploadCsv(e);
                  }}
                />
              </Button>
            </MenuItem>
          </Menu>
          <Menu anchorEl={anchorEl} open={openMenu} onClose={() => setAnchorEl(null)}>
            <MenuItem>
              <Stack spacing={1} sx={{ width: 320 }}>
                <Typography variant="body2">No filters available</Typography>
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button
                    onClick={() => {
                      setAnchorEl(null);
                    }}
                  >
                    Close
                  </Button>
                </Stack>
              </Stack>
            </MenuItem>
          </Menu>

          <TextField
            size="medium"
            placeholder="Search service types"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Toolbar>

        <Box sx={{ height: 560, width: "100%" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            getRowId={(r) => r.service_type_id || r.id}
            sx={{
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid #e0e0e0",
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f5f5f5",
                borderBottom: "2px solid #e0e0e0",
              },
            }}
          />
        </Box>
      </Paper>

      <Dialog
        open={open}
        onClose={() => {
          setOpen(false);
          setEditing(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{editing ? "Edit Service Type" : "Add Service Type"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Service Type Name"
              required
              fullWidth
              value={serviceTypeFormData.service_type_name}
              onChange={(e) =>
                setServiceTypeFormData({
                  ...serviceTypeFormData,
                  service_type_name: e.target.value,
                })
              }
            />
            <TextField
              label="Service Type Description"
              fullWidth
              value={serviceTypeFormData.service_type_desc}
              onChange={(e) =>
                setServiceTypeFormData({
                  ...serviceTypeFormData,
                  service_type_desc: e.target.value,
                })
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpen(false);
              setEditing(null);
            }}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit} sx={{ ml: 2 }}>
            {editing ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ServiceType -> ServiceCharges: View list dialog */}
      <Dialog
        open={scViewOpen}
        onClose={() => setSCViewOpen(false)}
        fullWidth
        maxWidth={false}
        sx={{
          "& .MuiDialog-paper": {
            width: "80%",
            maxWidth: "none",
            zIndex: 2000,
          },
        }}
      >
        <DialogTitle>
          Service Charges List of Service Type: {scServiceTypeName}
          <IconButton
            onClick={() => setSCViewOpen(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: 2 }}>
          <Box sx={{ height: 400, width: "100%" }}>
            <DataGrid
              rows={scRows}
              columns={scColumns}
              pageSize={10}
              rowsPerPageOptions={[10]}
              getRowId={(r) => r.service_charge_id || r.id}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button color="success" onClick={() => setSCViewOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ServiceType -> ServiceCharges: Create dialog */}
      <Dialog open={scDialogOpen} onClose={() => setSCDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {scEditing ? "Edit Service Charge" : `Add Service Charge for ${scServiceTypeName}`}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Academic Year"
              fullWidth
              value={serviceChargeFormData.academic_year}
              onChange={(e) =>
                setServiceChargeFormData({
                  ...serviceChargeFormData,
                  academic_year: e.target.value,
                })
              }
            />
            <TextField
              label="Service Description"
              required
              fullWidth
              value={serviceChargeFormData.service_description}
              onChange={(e) =>
                setServiceChargeFormData({
                  ...serviceChargeFormData,
                  service_description: e.target.value,
                })
              }
            />
            <TextField
              label="Service Charge"
              fullWidth
              type="number"
              value={serviceChargeFormData.service_charge}
              onChange={(e) => {
                const val = e.target.value;
                setServiceChargeFormData((prev) => ({ ...prev, service_charge: val }));
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSCDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleServiceChargeSave} sx={{ ml: 2 }}>
            {scEditing ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MuiAlert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}
